import { createSignal, onMount, Show } from "solid-js";
import { FileRoutes } from "@solidjs/start/router";
import c from "class-c";
import Cookies from "js-cookie";

import createAsync from "@repo/signals/createAsync";
import Button from "@repo/ui/buttons/Button";
import Banner from "@repo/ui/info/Banner";
import Link from "@repo/ui/navigation/Link";
import AppRouter from "@repo/utils-solid/AppRouter";

import SharedRootRouter from "~shared/SharedRootRouter";

import { useUser } from "./state/features/user";
import { useOverlaysBase } from "./overlays";
import { appApi } from "./rest";

import styles from "./app.module.scss";

export default function App() {
  return (
    <SharedRootRouter
      root={(props) => {
        onMount(() => {
          const utmContent = AppRouter.query.utm_content;
          if (utmContent) Cookies.set("utm_content", utmContent as string);
        });

        const overlaysBase = useOverlaysBase();
        const [user, setUser] = useUser();

        return (
          <>
            <Show when={user.impersonating()}>
              <Banner position="top" class={c`row-center bg:inverse`}>
                <div class={c`expand text-center color:strong-on-inverse pr-2`}>
                  You are in impersonation mode.
                </div>
                <Button
                  class="mr-4"
                  variant="primary-hard"
                  size="small"
                  onClick={() => setUser.logout()}
                >
                  Finish
                </Button>
              </Banner>
            </Show>
            {props.children}
            {overlaysBase.render()}
            {!import.meta.env.MOBILE &&
              (() => {
                const [consentCookie, setConsentCookie] = createSignal(
                  Cookies.get("consentCookie")
                );

                const showBanner = createAsync(async () => {
                  if (consentCookie()) return false;

                  return (await appApi.region.GET({})).region === "QC";
                });

                const handleClick = (cookieValue: string) => {
                  Cookies.set("consentCookie", cookieValue, {
                    path: "/",
                    expires: new Date("9999-12-31T23:59:59Z"),
                  });
                  setConsentCookie(cookieValue);
                };
                return (
                  <Show when={showBanner()}>
                    <Banner position="bottom" class={styles["cookie-banner"]}>
                      <div>
                        <div class="text:regular+ mb-1">Your privacy</div>
                        <div class="text:mini">
                          We use cookies to enhance your browsing experience,
                          serve personalized ads or content, and analyze our
                          traffic. By clicking "Accept all", you consent to our
                          use of cookies. Please read our{" "}
                          <Link.Doc href="/privacy-policy.pdf">
                            Privacy Policy
                          </Link.Doc>{" "}
                          for more information.
                        </div>
                      </div>
                      <div>
                        <Button
                          class="mr-1 underline"
                          variant="text"
                          size="small"
                          onClick={() => handleClick("optOut")}
                        >
                          Reject all
                        </Button>
                        <Button
                          variant="primary-hard"
                          size="small"
                          onClick={() => handleClick("optIn")}
                        >
                          Accept all
                        </Button>
                      </div>
                    </Banner>
                  </Show>
                );
              })()}
          </>
        );
      }}
    >
      <FileRoutes />
    </SharedRootRouter>
  );
}
